import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {PageGroupsObject} from '@PosterWhiteboard/page/page-groups.class';
import cloneDeep from 'lodash/cloneDeep';
import {getUniqueString} from '@Utils/string.util';
import {Alignment} from '@PosterWhiteboard/page/alignment.class';
import {getFabricObjectUID} from '@PosterWhiteboard/items/item/item.class';
import {FONT_SIZE_MAX, FONT_SIZE_MIN} from '@PosterWhiteboard/items/text-item/text-item.types';
import type {TBBox, TPointerEvent} from '@postermywall/fabricjs-2';
import {Point} from '@postermywall/fabricjs-2';
import {Group, ActiveSelection, FabricObject, Textbox, Canvas} from '@postermywall/fabricjs-2';
import type {AudioItem} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import {isChildOfGroup} from '@Utils/fabric.util';
import {commitPosterMutation} from '@PosterWhiteboard/poster/commit-poster-mutation';
import type {ClusterBounds} from '@PosterWhiteboard/page/selection-clusters.library';
import {getClusterBounds, getSelectionClusters, moveCluster} from '@PosterWhiteboard/page/selection-clusters.library';

export enum GroupSpacing {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export enum GroupSpacingAxis {
  X = 'x',
  Y = 'y',
}

const RESIZE_SELECTION_SCALE = {
  DEFAULT: 0.04,
  TEXT: 4,
};

interface DuplicateSelectionOpts {
  sourceGroupHashMap: PageGroupsObject['groupsHashMap'];
  x?: number;
  y?: number;
}

export enum ResizeSelectionKey {
  UP = 'up',
  DOWN = 'down',
}

export class SelectionManager {
  public page: Page;
  public alignment: Alignment;

  public constructor(page: Page) {
    this.page = page;
    this.alignment = new Alignment(this.page);
  }

  /**
   * Toggles an item in/out of the canvas multi-selection — the timeline's
   * modifier-click gesture. Backgrounds join like any other bar; only transcripts
   * drop out, they carry their own selection model.
   */
  public toggleItemInMultiSelection(item: ItemType): void {
    if (item.isTranscript()) {
      return;
    }
    const selectedItems = this.getTimelineSelectedItems();
    const isItemSelected = selectedItems.some((selectedItem) => selectedItem.uid === item.uid);
    this.setTimelineMultiSelection(isItemSelected ? selectedItems.filter((selectedItem) => selectedItem.uid !== item.uid) : [...selectedItems, item]);
  }

  /**
   * Sets the canvas selection to exactly `items` — the shared write path of the
   * timeline's toggle and shift-range selection gestures. The change runs with the
   * timeline-selection flag raised so the page's selection handlers keep the audio
   * half of a mixed timeline selection alive.
   */
  public setTimelineMultiSelection(items: ItemType[]): void {
    this.#isTimelineSelectionChange = true;
    try {
      if (items.length === 0) {
        this.discardActiveObject();
        this.page.fabricCanvas.requestRenderAll();
        return;
      }
      this.selectFabricObjects(items.map((item) => item.fabricObject));
    } finally {
      this.#isTimelineSelectionChange = false;
    }
  }

  /**
   * True while a timeline-originated selection write is in flight. Fabric fires its
   * selection events synchronously inside setActiveObject/discardActiveObject, so the
   * page's handlers read this instead of sniffing a sentinel event type.
   */
  public isTimelineSelectionChange(): boolean {
    return this.#isTimelineSelectionChange;
  }

  #isTimelineSelectionChange = false;

  /** The currently selected items that own a timeline bar — item-track items and backgrounds; transcripts excluded. */
  public getTimelineSelectedItems(): ItemType[] {
    const selectedItems: ItemType[] = [];
    for (const fabricObject of this.getActiveObjects()) {
      const item = this.page.items.getItem(getFabricObjectUID(fabricObject));
      if (item && !item.isTranscript()) {
        selectedItems.push(item);
      }
    }
    return selectedItems;
  }

  public onSelectionCreated(): void {
    const o = this.getActiveObject();
    if (o instanceof ActiveSelection) {
      const selectedViews = this.getSelectedViews() as (FabricObject | Group)[];

      for (let i = 0; i < selectedViews.length; i++) {
        const itemId = getFabricObjectUID(selectedViews[i]);
        const item = this.page.items.getItem(itemId);
        if (selectedViews[i] instanceof Textbox && item && (item.isText() || item.isSlideshow())) {
          selectedViews[i] = item.fabricObject;
          this.discardActiveObject();

          const selectedObjects = new ActiveSelection(selectedViews, {
            canvas: this.page.fabricCanvas,
          });
          this.setActiveObject(selectedObjects);
        }
      }
    }
  }

  public clearSelection(): void {
    const fabricObjects = this.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }

    this.discardActiveObject();
    this.page.fabricCanvas.requestRenderAll();
  }

  public removeItemFromSelection(item: FabricObject): void {
    const fabricObjects = this.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }

    this.selectFabricObjects(
      fabricObjects.filter((fabricObject) => {
        return !(fabricObject === item || (item instanceof Group && isChildOfGroup(fabricObject, item)));
      })
    );
  }

  public deleteSelectedItems(): void {
    const fabricObjects = this.getActiveObjects();
    if (!fabricObjects || fabricObjects.length === 0) {
      return;
    }

    const uids: string[] = [];

    for (let index = 0; index < fabricObjects.length; index += 1) {
      uids.push(fabricObjects[index].__PMWID);
    }

    this.page.poster.deleteItemByIds(uids);
    this.clearSelection();
  }

  /**
   * Distributes the selection so the gaps between adjacent clusters are equal along the given
   * axis. Clusters (saved groups and loose items) move as rigid units; the first and last
   * cluster anchor the two ends. `GroupSpacing.HORIZONTAL` evens out the vertical gaps of
   * stacked rows (Y axis) and `GroupSpacing.VERTICAL` the side-by-side gaps (X axis),
   * matching the historical button mapping.
   */
  #distributeClustersAlongAxis(axis: GroupSpacingAxis): void {
    const group = this.getActiveObject();
    if (!(group instanceof Group) || group.lockMovementX) {
      return;
    }

    const measuredClusters = getSelectionClusters(this.page).map((cluster) => {
      return {cluster, bounds: getClusterBounds(cluster)};
    });
    if (measuredClusters.length < 3) {
      return;
    }

    const getMin = (bounds: ClusterBounds): number => (axis === GroupSpacingAxis.X ? bounds.minX : bounds.minY);
    const getMax = (bounds: ClusterBounds): number => (axis === GroupSpacingAxis.X ? bounds.maxX : bounds.maxY);

    measuredClusters.sort((a, b) => {
      return getMin(a.bounds) - getMin(b.bounds);
    });

    const firstBounds = measuredClusters[0].bounds;
    const lastBounds = measuredClusters[measuredClusters.length - 1].bounds;
    const totalSpan = getMax(lastBounds) - getMin(firstBounds);
    const summedClusterSizes = measuredClusters.reduce((sum, measured) => {
      return sum + (getMax(measured.bounds) - getMin(measured.bounds));
    }, 0);
    const gap = (totalSpan - summedClusterSizes) / (measuredClusters.length - 1);

    let cursor = getMin(firstBounds);
    for (const measured of measuredClusters) {
      const delta = cursor - getMin(measured.bounds);
      moveCluster(measured.cluster, axis === GroupSpacingAxis.X ? delta : 0, axis === GroupSpacingAxis.Y ? delta : 0);
      cursor += getMax(measured.bounds) - getMin(measured.bounds) + gap;
    }

    commitPosterMutation(this.page);
    this.page.fabricCanvas.requestRenderAll();
  }

  public onGroupSpacing(type: GroupSpacing): void {
    switch (type) {
      case GroupSpacing.HORIZONTAL:
        this.#distributeClustersAlongAxis(GroupSpacingAxis.Y);
        break;
      case GroupSpacing.VERTICAL:
        this.#distributeClustersAlongAxis(GroupSpacingAxis.X);
        break;
      default:
        throw new Error('unhandled spacing type for grouped items');
    }
  }

  public onDuplicate(): void {
    const selectedItems = this.page.getSelectedItems();
    // A background duplicates sequentially — the clone slots in after the original —
    // so route it to the background path; everything else clones in place.
    if (selectedItems.length === 1 && selectedItems[0].isBackground) {
      void this.page.duplicateBackgroundAndSelect(selectedItems[0].uid);
      return;
    }
    if (selectedItems.length !== 0) {
      this.duplicateSelection(selectedItems, {sourceGroupHashMap: this.page.items.groups.groupsHashMap});
      return;
    }

    const selectedAudioItem = this.page.audioClips.getSelectedAudioItem();

    if (!selectedAudioItem) {
      return;
    }

    this.duplicateAudioItem(selectedAudioItem);
  }

  public duplicateAudioItem(audioItem: AudioItem): void {
    void this.page.items.addItems.addAudioItemFromAnotherAudioItem(audioItem);
  }

  public orderGraphicItems(graphicItems: ItemType[]): ItemType[] {
    const views = this.page.fabricCanvas.getObjects();
    const rearrangeItems = [];
    const graphicItemsNotOnFabric = [];
    const orderedGraphicItems = [];
    const graphicItemZIndexHashMap: Record<string, number> = {};

    for (let index = 0; index < views.length; index += 1) {
      const uid = views[index].__PMWID;
      graphicItemZIndexHashMap[uid] = index;
    }

    for (let index = 0; index < graphicItems.length; index += 1) {
      if (graphicItemZIndexHashMap[graphicItems[index].uid] !== undefined) {
        rearrangeItems[graphicItemZIndexHashMap[graphicItems[index].uid]] = graphicItems[index];
      } else {
        graphicItemsNotOnFabric.push(graphicItems[index]);
      }
    }

    for (let index = 0; index < rearrangeItems.length; index += 1) {
      if (rearrangeItems[index]) {
        orderedGraphicItems.push(rearrangeItems[index]);
      }
    }

    for (let index = 0; index < graphicItemsNotOnFabric.length; index += 1) {
      orderedGraphicItems.push(graphicItemsNotOnFabric[index]);
    }

    return orderedGraphicItems;
  }

  public duplicateSelection(graphicItems: ItemType[], {sourceGroupHashMap, x = 15, y = 15}: DuplicateSelectionOpts): void {
    if (graphicItems.length === 0) {
      return;
    }

    const items: ItemType[] = [];
    const itemsToDuplicate = this.orderGraphicItems(graphicItems);
    const groupIdMap = new Map<string, string>();

    for (let index = 0; index < itemsToDuplicate.length; index += 1) {
      const absoluteX = itemsToDuplicate[index].getX();
      const absoluteY = itemsToDuplicate[index].getY();
      const absoluteAngle = itemsToDuplicate[index].getAngle();
      const absoluteScaleX = itemsToDuplicate[index].getScaleX();
      const absoluteScaleY = itemsToDuplicate[index].getScaleY();

      const duplicateItem = cloneDeep(itemsToDuplicate[index]);
      duplicateItem.uid = getUniqueString();

      if (duplicateItem.groupId && itemsToDuplicate.length > 1) {
        let newGroupId = groupIdMap.get(duplicateItem.groupId);
        if (!newGroupId) {
          newGroupId = getUniqueString();
          groupIdMap.set(duplicateItem.groupId, newGroupId);
        }
        duplicateItem.groupId = newGroupId;
      } else {
        duplicateItem.groupId = null;
      }

      duplicateItem.fabricObject.group = undefined;
      duplicateItem.fabricObject.set({
        scaleX: absoluteScaleX,
        scaleY: absoluteScaleY,
        angle: absoluteAngle,
      });

      if (duplicateItem.isSlideshow()) {
        duplicateItem.selectedSlideUID = '';

        for (const [slideId, slide] of Object.entries(duplicateItem.slides.slidesHashMap)) {
          slide.uid = getUniqueString();
          duplicateItem.slides.slidesHashMap[slide.uid] = slide;
          delete duplicateItem.slides.slidesHashMap[slideId];

          const slideIndex = duplicateItem.slides.slidesOrder.indexOf(slideId);
          if (slideIndex !== -1) {
            duplicateItem.slides.slidesOrder[slideIndex] = slide.uid;
          }
        }
      }

      if (duplicateItem.isTranscript()) {
        for (const [subtitleId, subtitle] of Object.entries(duplicateItem.subtitlesHashmap)) {
          subtitle.subtitleUID = getUniqueString();
          duplicateItem.subtitlesHashmap[subtitle.subtitleUID] = subtitle;
          delete duplicateItem.subtitlesHashmap[subtitleId];
        }
      }

      if (x || y) {
        duplicateItem.fabricObject.setXY(new Point(absoluteX + x, absoluteY + y));
      }

      if (duplicateItem.isMenu()) {
        duplicateItem.updateCopiedItems(duplicateItem.itemIds);
      }

      duplicateItem.clearInProgressBackgroundRemoval();

      items.push(duplicateItem);
    }

    for (const [oldGroupId, newGroupId] of groupIdMap) {
      const oldRecord = sourceGroupHashMap[oldGroupId];
      if (oldRecord) {
        this.page.items.groups.groupsHashMap[newGroupId] = {...oldRecord};
      }
    }

    void this.page.items.addItems.addItemsFromItemType(items);
  }

  /**
   * The objects a marquee box over sceneRect would select: everything intersecting it, except
   * fully movement-locked objects (backgrounds, locked items) which are only ever
   * click/shift-click selected.
   */
  public getItemsIntersectingSceneRect(sceneRect: TBBox): FabricObject[] {
    if (!(this.page.fabricCanvas instanceof Canvas)) {
      return [];
    }
    return this.page.fabricCanvas
      .collectObjects(sceneRect, {includeIntersecting: true})
      .filter((fabricObject): fabricObject is FabricObject => fabricObject instanceof FabricObject && !(fabricObject.lockMovementX && fabricObject.lockMovementY));
  }

  /**
   * Forms the selection for a completed marquee drag. A box that catches nothing selectable
   * clears the selection, so a marquee swept over only locked objects never leaves them selected.
   */
  public selectItemsIntersectingSceneRect(sceneRect: TBBox, e?: TPointerEvent): void {
    if (!(this.page.fabricCanvas instanceof Canvas)) {
      return;
    }
    const collectedObjects = this.getItemsIntersectingSceneRect(sceneRect);
    if (collectedObjects.length === 0) {
      this.clearSelection();
      return;
    }
    const objectsToSelect = collectedObjects.length > 1 ? collectedObjects.filter((fabricObject) => !fabricObject.onSelect({e})).reverse() : collectedObjects;
    this.selectFabricObjects(objectsToSelect, e);
  }

  public selectFabricObjects(fabricObjects: FabricObject[], e?: TPointerEvent): void {
    this.discardActiveObject(e);
    if (fabricObjects.length === 1) {
      this.setActiveObject(fabricObjects[0], e);
    } else if (fabricObjects.length > 1) {
      for (const fabricObject of fabricObjects) {
        this.page.items.getItem(fabricObject.__PMWID)?.stopItemAnimation();
      }
      const groupId = this.page.items.groups.getGroupIdForFabricObjects(fabricObjects);
      if (groupId) {
        this.setActiveObject(this.page.items.groups.createActiveSelectionForGroup(fabricObjects, groupId), e);
      } else {
        this.setActiveObject(new ActiveSelection(fabricObjects, {canvas: this.page.fabricCanvas}), e);
      }
    }
    this.page.fabricCanvas.requestRenderAll();
  }

  public async resizeActiveSelection(e: Event, key = ResizeSelectionKey.UP): Promise<void> {
    const direction = key === ResizeSelectionKey.DOWN ? -1 : 1;

    const activeItems = this.page.getSelectedItems();
    if (!activeItems?.length) {
      return;
    }

    const scale = RESIZE_SELECTION_SCALE.DEFAULT * direction;
    const textFontSize = RESIZE_SELECTION_SCALE.TEXT * direction;

    if (activeItems.length === 1) {
      e.preventDefault();

      if (activeItems[0].fabricObject.lockMovementX) {
        return;
      }

      if (
        activeItems[0].isText() &&
        ((direction === -1 && activeItems[0].textStyles.fontSize > FONT_SIZE_MIN) || (direction === 1 && activeItems[0].textStyles.fontSize < FONT_SIZE_MAX))
      ) {
        let newFontSize = activeItems[0].textStyles.fontSize + textFontSize;
        if (newFontSize < FONT_SIZE_MIN) {
          newFontSize = FONT_SIZE_MIN;
        } else if (newFontSize > FONT_SIZE_MAX) {
          newFontSize = FONT_SIZE_MAX;
        }

        void activeItems[0].updateFromObject({
          textStyles: {
            fontSize: newFontSize,
          },
          width: activeItems[0].fabricObject.width * (newFontSize / activeItems[0].textStyles.fontSize),
        });
      } else if (
        !activeItems[0].isText() &&
        ((activeItems[0].fabricObject.getObjectScaling().x > RESIZE_SELECTION_SCALE.DEFAULT && activeItems[0].fabricObject.getObjectScaling().y > RESIZE_SELECTION_SCALE.DEFAULT) ||
          direction === 1)
      ) {
        void activeItems[0].updateFromObject({
          scaleX: activeItems[0].fabricObject.getObjectScaling().x + scale,
          scaleY: activeItems[0].fabricObject.getObjectScaling().y + scale,
        });
      }
    } else if (activeItems.length > 1) {
      const group = this.getActiveObject();
      if (!group) {
        return;
      }

      if (group.lockMovementX) {
        return;
      }

      if ((group.scaleX > RESIZE_SELECTION_SCALE.DEFAULT && group.scaleY > RESIZE_SELECTION_SCALE.DEFAULT) || direction === 1) {
        await this.updateGroupSelection({
          scaleX: group.scaleX + scale,
          scaleY: group.scaleY + scale,
        });
      }
    }
  }

  public async updateGroupSelection(changes: Record<string, any>, undoable = true): Promise<void> {
    const group = this.getActiveObject();
    if (group instanceof Group) {
      group.set(changes);
      group.setCoords();
      if (undoable) {
        this.page.poster.history.addPosterHistory();
      }
      this.page.poster.redux.updateReduxData();
      this.page.fabricCanvas.requestRenderAll();
    }
  }

  public setActiveObject(object: FabricObject, e?: TPointerEvent): void {
    if (this.page.fabricCanvas instanceof Canvas) {
      this.page.fabricCanvas.setActiveObject(object, e);
      this.page.fabricCanvas.requestRenderAll();
    }
  }

  public getActiveObject(): FabricObject | undefined {
    return this.page.fabricCanvas instanceof Canvas ? this.page.fabricCanvas.getActiveObject() : undefined;
  }

  public discardActiveObject(e?: TPointerEvent): void {
    if (this.page.fabricCanvas instanceof Canvas) {
      this.page.fabricCanvas.discardActiveObject(e);
    }
  }

  public getActiveObjects(): FabricObject[] {
    return this.page.fabricCanvas instanceof Canvas ? this.page.fabricCanvas.getActiveObjects() : [];
  }

  public getSelectedViews(): FabricObject[] {
    const o = this.getActiveObject();
    let views: FabricObject[] = [];
    if (o) {
      if (o instanceof ActiveSelection) {
        views = o.getObjects();
      } else {
        views[0] = o;
      }
    }
    return views;
  }
}
